import { ApiFetchError, getApiBase, getBrowserTimezone } from "./api";

/** Send the original Blob with cookie authentication and native byte progress. */
export function uploadRawBlob(
  path: string,
  body: Blob,
  headers: Record<string, string>,
  onProgress: (loaded: number, total: number) => void,
  signal?: AbortSignal,
): Promise<unknown> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Upload cancelled", "AbortError"));
      return;
    }
    const request = new XMLHttpRequest();
    const abort = () => request.abort();
    const cleanup = () => signal?.removeEventListener("abort", abort);
    request.open("POST", `${getApiBase()}${path}`);
    request.withCredentials = true;
    request.setRequestHeader("X-Timezone", getBrowserTimezone());
    for (const [name, value] of Object.entries(headers)) {
      request.setRequestHeader(name, value);
    }
    request.upload.onprogress = (event) => {
      if (!signal?.aborted) onProgress(Math.min(event.loaded, body.size), body.size);
    };
    request.onload = () => {
      cleanup();
      if (signal?.aborted) {
        reject(new DOMException("Upload cancelled", "AbortError"));
        return;
      }
      if (request.status < 200 || request.status >= 300) {
        reject(new ApiFetchError(request.status, request.responseText));
        return;
      }
      try {
        resolve(JSON.parse(request.responseText));
      } catch (error) {
        reject(error);
      }
    };
    request.onerror = () => {
      cleanup();
      reject(new TypeError("Failed to fetch"));
    };
    request.onabort = () => {
      cleanup();
      reject(new DOMException("Upload aborted", "AbortError"));
    };
    signal?.addEventListener("abort", abort, { once: true });
    request.send(body);
  });
}
